import { useCallback, useEffect, useState } from "react";
import { Files, Moon, Network, Search, Sun } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { FileTree } from "./FileTree";
import { GraphCanvas } from "./GraphCanvas";
import { NoteView } from "./NoteView";
import { SearchDialog } from "./SearchDialog";
import { useLiveSnapshot } from "./useLiveSnapshot";

function decodeNotePath(pathname: string): string | undefined {
  if (pathname === "/" || pathname === "/graph") return undefined;
  try { return decodeURIComponent(pathname.slice(1)).normalize("NFC"); } catch { return undefined; }
}

function notePath(id: string): string {
  return `/${id.split("/").map(encodeURIComponent).join("/")}`;
}

// Во время загрузки ширина окна может быть ещё неизвестна (0). Нулевую ширину считаем
// широким экраном, иначе проводник ошибочно стартовал бы в мобильном режиме.
function isNarrowViewport(): boolean {
  return window.innerWidth > 0 && window.innerWidth <= 720;
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const noteId = decodeNotePath(location.pathname);
  const editing = Boolean(noteId && new URLSearchParams(location.search).get("edit") === "1");
  const { manifest, graph, lastEvent, connected, error } = useLiveSnapshot();
  const [searchOpen, setSearchOpen] = useState(false);
  // На узком экране проводник — выдвижная панель поверх содержимого, поэтому по умолчанию закрыт.
  const [explorerOpen, setExplorerOpen] = useState(() => !isNarrowViewport());
  const [theme, setTheme] = useState<"dark" | "light">(() => (localStorage.getItem("theme") as "dark" | "light") || "dark");
  const openNote = useCallback((id: string) => {
    navigate(notePath(id));
    if (isNarrowViewport()) setExplorerOpen(false);
  }, [navigate]);
  const openGraph = useCallback(() => navigate("/"), [navigate]);
  const openEditor = useCallback(() => noteId && navigate(`${notePath(noteId)}?edit=1`), [navigate, noteId]);
  const closeEditor = useCallback(() => noteId && navigate(notePath(noteId), { replace: true }), [navigate, noteId]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === "o") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className={`app-shell ${explorerOpen ? "with-explorer" : ""}`}>
      <aside className="ribbon" aria-label="Навигация">
        <div className="vault-mark" aria-label="Зазеркалье"><img src="/site-icon.png" alt="" /></div>
        <nav>
          <button className={`ribbon-button ${explorerOpen ? "active" : ""}`} onClick={() => setExplorerOpen((open) => !open)} aria-label="Список заметок" aria-pressed={explorerOpen}><Files size={20} /></button>
          <button className={`ribbon-button ${!noteId ? "active" : ""}`} onClick={openGraph} aria-label="Граф"><Network size={20} /></button>
          <button className="ribbon-button" onClick={() => setSearchOpen(true)} aria-label="Поиск"><Search size={20} /></button>
        </nav>
        <button className="ribbon-button theme-button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Сменить тему">
          {theme === "dark" ? <Sun size={19} /> : <Moon size={19} />}
        </button>
      </aside>
      {explorerOpen ? (
        <>
          <aside className="explorer" aria-label="Проводник">
            {graph ? <FileTree nodes={graph.nodes} activeId={noteId} onOpen={openNote} /> : <p className="explorer-empty">Загружаем список…</p>}
          </aside>
          <button type="button" className="explorer-scrim" aria-label="Закрыть список" onClick={() => setExplorerOpen(false)} />
        </>
      ) : null}
      <section className="workspace">
        <header className="workspace-header">
          <div><strong>Зазеркалье</strong><span>{editing ? "Редактор заметки" : noteId ? noteId.split("/").at(-1) : "Глобальный граф"}</span></div>
          <div className="live-state"><i className={connected ? "online" : "offline"} />{connected ? "Обновляется автоматически" : "Переподключение…"}</div>
        </header>
        <div className="workspace-content">
          {noteId ? (
            <NoteView id={noteId} version={manifest?.version} event={lastEvent} editing={editing} nodes={graph?.nodes ?? []} onGraph={openGraph} onNavigate={openNote} onEdit={openEditor} onCloseEdit={closeEditor} />
          ) : graph ? (
            <GraphCanvas graph={graph} onOpen={openNote} />
          ) : (
            <div className="graph-loading"><span className="orb" /><p>{error ?? "Собираем Зазеркалье…"}</p></div>
          )}
        </div>
        <footer className="status-bar">
          <span>{manifest ? `${manifest.noteCount} заметок · ${manifest.linkCount} связей` : "Подключение к vault"}</span>
        </footer>
      </section>
      <SearchDialog open={searchOpen} version={manifest?.version} onClose={() => setSearchOpen(false)} onOpen={openNote} />
    </div>
  );
}
